package fyi.moko.hotpl.ui.directory

import fyi.moko.hotpl.domain.model.GeoPoint
import fyi.moko.hotpl.domain.model.PlaceCategory
import kotlin.math.absoluteValue
import kotlin.math.roundToInt

// Pure label helpers, kept out of DirectoryScreen so they can be tested without Compose.

/** Says what the list is promising: nearest-first, and of what kind if a category is on. */
internal fun nearestResultsHeader(nearestFirst: Boolean, category: PlaceCategory?): String = when {
    !nearestFirst -> "Online Results"
    category == null -> "Nearest First"
    else -> "Nearest " + category.label
}

/**
 * Distance for a prediction row: metres below a kilometre, one decimal up to 10 km, whole
 * kilometres beyond that — precision the reader can act on, without implying the
 * straight-line figure is a walking distance.
 */
internal fun formatDistance(meters: Int): String = when {
    meters < 1_000 -> "$meters m"
    meters < 10_000 -> {
        // Built from Ints rather than letting a Double print itself: Kotlin/JS renders 1.0
        // as "1", so the web dropped the decimal the phone showed. Caught by jsBrowserTest,
        // not by the host tests — the same divergence KtorPlaceSearchProviderTest already
        // works around when it decodes request bodies instead of string-matching them.
        val tenths = (meters / 100.0).roundToInt()
        "${tenths / 10}.${tenths % 10} km"
    }
    else -> "${(meters / 1_000.0).roundToInt()} km"
}

/**
 * The point every distance on this screen is measured from, or a reason there isn't one.
 *
 * 좌표를 그대로 보여 주는 이유는 알파이기 때문이다. "가까운 순"이 이상하게 나올 때 그게 정렬
 * 버그인지 위치가 엉뚱한 곳으로 잡힌 것인지, 리뷰어가 화면만 보고 구분할 수 있어야 한다 —
 * 실제로 이 값을 몰라서 거리 목록을 역산해 원점을 추정한 적이 있다.
 *
 * 위치가 없을 때 침묵하지 않는 것도 같은 이유다. 웹에서 브라우저 위치 권한이 거부돼 있으면
 * 결과가 조용히 전 세계로 퍼지는데, 화면은 그걸 알려주지 않았다.
 */
internal fun originLabel(origin: GeoPoint?, nearbyBrowseAvailable: Boolean = true): String =
    if (origin == null) {
        // "Near me"를 감춘 화면에서는 그 이름을 꺼내지 않는다 — 없는 버튼을 근거로 드는 꼴이다.
        if (nearbyBrowseAvailable) {
            "Location unavailable — distances and \"Near me\" need it"
        } else {
            "Location unavailable — distances need it"
        }
    } else {
        "Around " + formatCoordinate(origin.latitude) + ", " + formatCoordinate(origin.longitude) +
            " · closest first"
    }

/**
 * 4 decimal places (≈11 m), built from Ints.
 *
 * `Double.toString()`을 쓰지 않는다 — Kotlin/JS는 `34.0`을 `"34"`로 렌더링해서 웹에서만 자릿수가
 * 사라진다. 같은 함정을 [formatDistance]에서 이미 한 번 밟았다.
 */
internal fun formatCoordinate(value: Double): String {
    val scaled = (value * 10_000).roundToInt()
    val sign = if (scaled < 0) "-" else ""
    val magnitude = scaled.absoluteValue
    return sign + (magnitude / 10_000) + "." + (magnitude % 10_000).toString().padStart(4, '0')
}
